import type { LandingPageContent } from '../types'

// GitLab-Issue #144: polnische Übersetzung, gleiche topicId wie DE/ES. Screenshots wie bei der
// PL-Kundenseite dieselben Bilddateien aus einer deutschsprachigen Test-Session.
export const JAK_DZIALA_DLA_INSTALATOROW: LandingPageContent = {
  slug: 'jak-dziala-dla-instalatorow',
  lang: 'pl',
  topicId: 'so-funktioniert-es-fuer-handwerker',
  title: 'Jak działa Roofora dla firm instalatorskich – instrukcja krok po kroku',
  metaDescription:
    'Instrukcja krok po kroku dla firm instalatorskich: założenie konta, przeglądanie ' +
    'zanonimizowanych zapytań, złożenie bezpłatnej oferty i poproszenie o kontakt z klientem.',
  keywords: [
    'Roofora instrukcja dla instalatorów',
    'Roofora jak to działa dla firm instalatorskich',
    'przeglądanie zapytań fotowoltaicznych',
    'złożenie oferty fotowoltaicznej instrukcja',
    'poproszenie o kontakt z klientem solarnym',
  ],
  eyebrow: 'Instrukcja dla firm instalatorskich',
  h1: 'Jak działa Roofora dla firm instalatorskich',
  intro:
    'Ta instrukcja pokazuje każdy krok z perspektywy firmy instalatorskiej - od rejestracji, przez ' +
    'listę zanonimizowanych zapytań, po złożenie oferty i prośbę o kontakt - z prawdziwymi zrzutami ' +
    'ekranu z aplikacji. Zrzuty pochodzą z sesji testowej w języku niemieckim; aplikacja dostępna ' +
    'jest też po polsku.',
  sections: [
    { type: 'heading', text: 'Wyjaśnione w 3 minuty' },
    {
      type: 'paragraph',
      text:
        'Zakładasz bezpłatne konto firmowe i przeglądasz zanonimizowane zapytania solarne z Twojego ' +
        'regionu - z danymi dachu, zużyciem i zdjęciami, ale bez danych kontaktowych klienta. ' +
        'Składasz bezpłatną, niewiążącą ofertę i jednocześnie prosisz o kontakt. Dopiero gdy klient ' +
        'jednoznacznie zaakceptuje właśnie Twoją firmę, otrzymujesz e-mail i/lub numer telefonu.',
    },
    { type: 'heading', text: 'Co jest potrzebne wcześniej?' },
    {
      type: 'list',
      items: [
        'Nazwa firmy',
        'Nie jest potrzebny e-mail ani numer telefonu, wystarczy nazwa użytkownika i hasło',
        'Do przekonującej oferty: orientacyjna cena i opcjonalnie oferta w formacie PDF',
      ],
    },
  ],
  steps: [
    {
      title: 'Start i rejestracja',
      whatYouSee:
        'Na stronie rejestracji dla firm instalatorskich formularz z nazwą firmy, nazwą użytkownika i ' +
        'hasłem, a także krótkie podsumowanie: przeglądanie zapytań, składanie oferty, prośba o kontakt.',
      whatToDo:
        'Kliknij przycisk dla instalatorów na stronie głównej, podaj nazwę firmy, wybierz nazwę ' +
        'użytkownika i hasło o długości co najmniej 8 znaków, po czym zakończ rejestrację.',
      whatHappensNext: 'Tak jak przy koncie klienta, otrzymasz kod odzyskiwania - nie ma resetu hasła przez e-mail.',
      successCriterion: 'Trafiasz do panelu instalatora.',
      screenshot: {
        src: '/how-to/handwerker/01-registrierung.jpg',
        alt: 'Formularz rejestracji dla instalatorów z nazwą firmy, nazwą użytkownika i hasłem',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Panel instalatora',
      whatYouSee:
        'Nazwa Twojej firmy, identyfikator oraz cztery kafelki - „Otwarte zapytania” (z liczbą nowych ' +
        'zapytań), „Moje zapytania o kontakt”, „Lista ulubionych” i „Rynek B2B”.',
      whatToDo: 'Kliknij kafelek otwartych zapytań.',
      whatHappensNext: 'Otwiera się zanonimizowana lista wszystkich otwartych zapytań solarnych.',
      successCriterion: 'Widoczny jest widok „Otwarte zapytania (zanonimizowane)”.',
      screenshot: {
        src: '/how-to/handwerker/02-dashboard.jpg',
        alt: 'Panel instalatora z kafelkami Otwarte zapytania, Moje zapytania o kontakt, Ulubione, Rynek B2B',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Przeszukiwanie otwartych zapytań',
      whatYouSee:
        'Tabela z zanonimizowanymi zapytaniami - typ zlecenia, kod pocztowy, daty, parametry ' +
        'instalacji, liczba złożonych już ofert i liczba udostępnień. Powyżej filtry według typu ' +
        'zlecenia, kraju, kodu pocztowego i identyfikatora.',
      whatToDo: 'Filtruj według kodu pocztowego lub typu zlecenia, aby znaleźć pasujące zapytania z Twojego regionu, i rozwiń wiersz strzałką.',
      whatHappensNext: 'Widzisz wyłącznie liczbę już złożonych ofert, nie ich wysokość - firmy składają oferty niezależnie od siebie.',
      successCriterion: 'Na liście widoczne jest co najmniej jedno pasujące zapytanie.',
      screenshot: {
        src: '/how-to/handwerker/03-offene-anfragen-liste.jpg',
        alt: 'Tabela otwartych, zanonimizowanych zapytań z filtrami według kodu pocztowego i typu zlecenia',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Sprawdzenie szczegółów zapytania',
      whatYouSee:
        'Po rozwinięciu wszystkie istotne dane w formie etykiet - typ budynku, połacie dachu, ' +
        'kształt i materiał dachu, rok budowy, okablowanie, miejsce montażu falownika/magazynu ' +
        'energii, liczba osób w gospodarstwie, zużycie, preferencja magazynu, chęć kontaktu wraz z ' +
        'przedziałem czasowym - a także przesłane zdjęcia dachu i skrzynki licznikowej. Bez danych ' +
        'kontaktowych klienta.',
      whatToDo: 'Sprawdź, czy zapytanie pasuje do Twojej firmy (region, typ zlecenia, stan dachu na zdjęciach).',
      whatHappensNext: 'Jeśli tak, przewiń do formularza oferty tuż pod szczegółami.',
      successCriterion: 'Masz wystarczająco informacji, aby oszacować realistyczną cenę.',
      screenshot: {
        src: '/how-to/handwerker/04-anfrage-detail.jpg',
        alt: 'Rozwinięte szczegóły zapytania z etykietami budynku, dachu, zużycia i przesłanych zdjęć',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Złożenie oferty i prośba o kontakt',
      whatYouSee:
        'Pole na szacunkową cenę, opcjonalny wybór producenta, pole tekstowe „Dlaczego klient powinien ' +
        'wybrać Ciebie?” oraz możliwość dołączenia oferty w PDF, a także przycisk złożenia oferty i ' +
        'prośby o kontakt.',
      whatToDo:
        'Podaj szacunkową cenę, opcjonalnie dodaj krótki wyróżnik swojej firmy i potwierdź wysłanie ' +
        'oferty oraz prośby o kontakt.',
      whatHappensNext:
        'Twoja oferta i prośba o kontakt są wysyłane w jednym kroku - obie czynności są dla Ciebie ' +
        'bezpłatne i niewiążące. Klient decyduje indywidualnie, czy udostępni Ci kontakt.',
      successCriterion: 'Pojawia się zielone potwierdzenie wysłania oferty.',
      troubleshooting:
        'Nie wpisuj w polu tekstowym własnych danych kontaktowych (e-mail, telefon) - powoduje to ' +
        'błąd. Oferty z załączonym PDF są zazwyczaj częściej akceptowane.',
      screenshot: {
        src: '/how-to/handwerker/05-angebot-formular.jpg',
        alt: 'Wypełniony formularz oferty z ceną i tekstem uzasadnienia, przycisk wysłania oferty i prośby o kontakt',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Potwierdzenie i dalsze kroki',
      whatYouSee: 'Zielony komunikat potwierdzenia nad listą zapytań (teraz pustą).',
      whatToDo: 'Poczekaj na decyzję klienta - w „Moich zapytaniach o kontakt” w panelu widzisz status wszystkich wysłanych próśb.',
      whatHappensNext: 'Jeśli klient jednoznacznie zaakceptuje Twoją firmę, otrzymasz tam e-mail i/lub telefon oraz preferowany przedział czasowy.',
      successCriterion: 'Oferta pojawia się w „Moich zapytaniach o kontakt” z odpowiednim statusem.',
      troubleshooting: 'Bez zapisanego adresu e-mail nie otrzymasz aktywnego powiadomienia - warto regularnie sprawdzać konto.',
      screenshot: {
        src: '/how-to/handwerker/06-angebot-gesendet.jpg',
        alt: 'Komunikat potwierdzający wysłanie oferty na liście zapytań',
        width: 1120,
        height: 567,
      },
    },
  ],
  faq: [
    {
      q: 'Czy korzystanie z Roofora jako instalator jest płatne?',
      a: 'Nie. Rejestracja, przeglądanie zapytań, składanie ofert i prośba o kontakt są obecnie bezpłatne.',
    },
    {
      q: 'Czy widzę ceny innych firm instalatorskich?',
      a: 'Nie. Widzisz tylko liczbę już złożonych ofert, nie ich wysokość - każda firma składa ofertę niezależnie.',
    },
    {
      q: 'Czy automatycznie otrzymuję dane kontaktowe po złożeniu oferty?',
      a: 'Nie. Składając ofertę, jedynie prosisz o kontakt. Dane otrzymasz dopiero, gdy klient jednoznacznie zaakceptuje Twoją firmę.',
    },
    {
      q: 'Czy mogę później zmienić swoją ofertę?',
      a: 'Tak, dopóki zapytanie jest otwarte, możesz w każdej chwili dostosować lub poprawić swoją ofertę.',
    },
    {
      q: 'Co się stanie, jeśli klient nie zaakceptuje mojej prośby o kontakt?',
      a: 'Nie ponosisz żadnych kosztów ani zobowiązań - możesz nadal przeglądać inne pasujące zapytania z Twojego regionu.',
    },
  ],
  primaryCta: { label: 'Zarejestruj się bezpłatnie jako instalator', action: 'craftsman' },
  secondaryCta: { label: 'Zobacz instrukcję dla klientów', action: 'link', href: '/jak-dziala-dla-klientow' },
  relatedLinks: [
    { label: 'Dla instalatorów: znajdź więcej zleceń', href: '/zlecenia-fotowoltaiczne-dla-instalatorow' },
    { label: 'Jak działa Roofora dla klientów', href: '/jak-dziala-dla-klientow' },
  ],
}
